import type { MetricFormat, MetricValue, Provenance } from "@t3tools/contracts";
import { CheckIcon, CircleAlertIcon, SendIcon, SquareIcon, Undo2Icon, XIcon } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from "react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Spinner } from "~/components/ui/spinner";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";

import { formatMetricValue } from "../artifactFormat";
import { artifactTrust } from "../artifactTrust";
import { ArtifactTrustBadge, ArtifactWidgetFreshness } from "../ArtifactTrustPopover";
import type { DashboardAssistantMode } from "./dashboardAssistantPrompts";
import type {
  DashboardAssistantItem,
  DashboardAssistantThreadState,
} from "./dashboardAssistantThread";

type EditsItem = Extract<DashboardAssistantItem, { kind: "edits" }>;

export type DashboardAssistantPanelProps = {
  /** The composer's element id, so `/` can focus it from anywhere on the page. */
  composerId: string;
  /** "edit" for people who can change the dashboard; "ask" gets answers only. */
  mode: DashboardAssistantMode;
  onCancel: () => void;
  onClose?: (() => void) | undefined;
  /** Shows a version on the page, in place. */
  onOpenVersion: (version: number) => void;
  onSend: (text: string) => void;
  onUndo: (item: EditsItem) => void;
  prompts: ReadonlyArray<string>;
  state: DashboardAssistantThreadState;
  versionHref: (version: number) => string;
  className?: string;
};

const NUMBER = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

function formatPeriod(period: MetricValue["period"]): string {
  const start = DAY.format(new Date(`${period.start}T00:00:00Z`));
  if (period.start === period.end) return start;
  return `${start} – ${DAY.format(new Date(`${period.end}T00:00:00Z`))}`;
}

function metricFormat(
  provenance: ReadonlyArray<Provenance>,
  metricId: string,
): MetricFormat | undefined {
  const found = provenance.find((entry) => entry.kind === "metric" && entry.metricId === metricId);
  return found?.kind === "metric" ? found.format : undefined;
}

function metricTitle(provenance: ReadonlyArray<Provenance>, metricId: string): string {
  const found = provenance.find((entry) => entry.kind === "metric" && entry.metricId === metricId);
  return found?.kind === "metric" ? found.title : metricId;
}

function VersionLink(props: {
  onOpenVersion: (version: number) => void;
  version: number;
  versionHref: (version: number) => string;
}) {
  return (
    <a
      className="font-medium text-foreground underline underline-offset-2"
      href={props.versionHref(props.version)}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        props.onOpenVersion(props.version);
      }}
    >
      Version {props.version}
    </a>
  );
}

function EditsChip(props: {
  item: EditsItem;
  mode: DashboardAssistantMode;
  onOpenVersion: (version: number) => void;
  onUndo: (item: EditsItem) => void;
  versionHref: (version: number) => string;
}) {
  const { item } = props;
  const { undo } = item;
  return (
    <div
      className="space-y-1.5 rounded-lg border border-success/32 bg-success/4 p-2.5 text-sm"
      data-assistant-edits={item.version}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="success">
          <CheckIcon aria-hidden />
          Edits applied
        </Badge>
        <VersionLink
          onOpenVersion={props.onOpenVersion}
          version={item.version}
          versionHref={props.versionHref}
        />
        {props.mode === "edit" && undo.state !== "done" ? (
          <Button
            className="ms-auto"
            disabled={undo.state === "pending"}
            onClick={() => props.onUndo(item)}
            size="xs"
            variant="outline"
          >
            {undo.state === "pending" ? <Spinner aria-hidden /> : <Undo2Icon aria-hidden />}
            {undo.state === "pending" ? "Undoing" : "Undo"}
          </Button>
        ) : null}
      </div>
      <p className="text-muted-foreground">{item.summary}</p>
      {undo.state === "done" ? (
        <p className="text-muted-foreground">
          Undone, saved as{" "}
          <VersionLink
            onOpenVersion={props.onOpenVersion}
            version={undo.version}
            versionHref={props.versionHref}
          />
          .
        </p>
      ) : null}
      {undo.state === "failed" ? (
        <p role="alert" className="text-destructive-foreground">
          {undo.message}
        </p>
      ) : null}
    </div>
  );
}

function AnswerCard(props: { item: Extract<DashboardAssistantItem, { kind: "answer" }> }) {
  const { item } = props;
  const trust = artifactTrust(item, new Date());
  return (
    <div className="space-y-2 rounded-lg border border-border bg-background p-2.5 text-sm">
      <dl className="space-y-1">
        {item.values.map((value) => (
          <div
            key={`${value.metricId}:${value.period.start}:${JSON.stringify(value.dimensions)}`}
            className="flex items-baseline justify-between gap-3"
          >
            <dt className="text-muted-foreground">
              {metricTitle(item.provenance, value.metricId)}, {formatPeriod(value.period)}
            </dt>
            <dd className="font-semibold text-foreground tabular-nums">
              {value.value === null
                ? "Hidden"
                : formatMetricValue(value.value, metricFormat(item.provenance, value.metricId))}
            </dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <ArtifactTrustBadge label={trust.label} level={trust.level} />
        <ArtifactWidgetFreshness trust={trust} />
      </div>
    </div>
  );
}

/** One entry of an assistant thread: a message, a step, an answer, saved edits or an error. */
export function DashboardAssistantThreadItem(props: {
  item: DashboardAssistantItem;
  mode: DashboardAssistantMode;
  onOpenVersion: (version: number) => void;
  onUndo: (item: EditsItem) => void;
  versionHref: (version: number) => string;
}) {
  const { item } = props;
  switch (item.kind) {
    case "user":
      return (
        <p className="ms-8 self-end rounded-lg bg-secondary px-3 py-2 text-sm whitespace-pre-wrap text-secondary-foreground">
          {item.text}
        </p>
      );
    case "message":
      return (
        <p
          aria-busy={item.streaming || undefined}
          className="text-sm leading-relaxed whitespace-pre-wrap text-foreground"
        >
          {item.text}
          {item.streaming ? (
            <span
              aria-hidden
              className="ms-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-muted-foreground/60 align-middle"
            />
          ) : null}
        </p>
      );
    case "activity":
      return (
        <p
          className="flex items-center gap-2 text-xs text-muted-foreground"
          data-activity-state={item.state}
        >
          {item.state === "running" ? (
            <Spinner className="size-3.5" aria-hidden />
          ) : item.state === "done" ? (
            <CheckIcon className="size-3.5 text-success" aria-hidden />
          ) : (
            <XIcon className="size-3.5 text-destructive" aria-hidden />
          )}
          {item.text}
        </p>
      );
    case "edits":
      return (
        <EditsChip
          item={item}
          mode={props.mode}
          onOpenVersion={props.onOpenVersion}
          onUndo={props.onUndo}
          versionHref={props.versionHref}
        />
      );
    case "answer":
      return <AnswerCard item={item} />;
    case "error":
      return (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/32 bg-destructive/4 p-2.5 text-sm"
        >
          <CircleAlertIcon className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <div className="min-w-0 space-y-1">
            <p className="text-foreground">{item.plain}</p>
            {item.details ? (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Details</summary>
                <p className="mt-1 break-words whitespace-pre-wrap">{item.details}</p>
              </details>
            ) : null}
          </div>
        </div>
      );
  }
}

/**
 * The assistant beside a dashboard: this person's thread, example prompts
 * named after the dashboard's own tiles, and a composer. Presentational; the
 * page owns the thread and applies its edits.
 */
export function DashboardAssistantPanel(props: DashboardAssistantPanelProps) {
  const { mode, state } = props;
  const [draft, setDraft] = useState("");
  const log = useRef<HTMLDivElement>(null);
  const running = state.running !== null;
  const title = mode === "edit" ? "Assistant" : "Ask a question";
  const lastQuestion = [...state.items].reverse().find((item) => item.kind === "user");
  const failed = !running && state.items.at(-1)?.kind === "error";

  useEffect(() => {
    const element = log.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [state.items]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (running || !draft.trim()) return;
    props.onSend(draft);
    setDraft("");
  };

  const usePrompt = (prompt: string) => {
    setDraft(prompt);
    document.getElementById(props.composerId)?.focus();
  };

  return (
    <section
      aria-label={title}
      className={cn("flex h-full min-h-0 flex-col bg-card text-card-foreground", props.className)}
      data-assistant-mode={mode}
    >
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {props.onClose ? (
          <Button
            aria-label="Close assistant"
            className="ms-auto"
            onClick={props.onClose}
            size="icon-xs"
            variant="ghost"
          >
            <XIcon aria-hidden />
          </Button>
        ) : null}
      </header>
      <div
        ref={log}
        role="log"
        aria-label={`${title} thread`}
        aria-live="polite"
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3"
      >
        {state.items.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {mode === "edit"
                ? "Ask about this dashboard, or tell me what to change. Edits save as a new version you can undo."
                : "Ask about the numbers on this dashboard. You can read answers here; only its owner can change it."}
            </p>
            {props.prompts.length ? (
              <ul aria-label="Example prompts" className="flex flex-col items-start gap-1.5">
                {props.prompts.map((prompt) => (
                  <li key={prompt}>
                    <Button
                      className="h-auto py-1 text-start whitespace-normal"
                      onClick={() => usePrompt(prompt)}
                      size="sm"
                      variant="outline"
                    >
                      {prompt}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          state.items.map((item) => (
            <DashboardAssistantThreadItem
              key={item.id}
              item={item}
              mode={mode}
              onOpenVersion={props.onOpenVersion}
              onUndo={props.onUndo}
              versionHref={props.versionHref}
            />
          ))
        )}
        {failed && lastQuestion ? (
          <Button
            className="self-start"
            onClick={() => props.onSend(lastQuestion.text)}
            size="sm"
            variant="outline"
          >
            Try again
          </Button>
        ) : null}
      </div>
      <form className="shrink-0 space-y-2 border-t border-border p-3" onSubmit={submit}>
        <label className="sr-only" htmlFor={props.composerId}>
          {mode === "edit" ? "Message the assistant" : "Ask a question"}
        </label>
        <Textarea
          id={props.composerId}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              submit(event);
            }
          }}
          placeholder={
            mode === "edit" ? "Ask, or describe a change" : "Ask a question about this dashboard"
          }
          rows={2}
          size="sm"
          value={draft}
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            Press <kbd className="font-sans">/</kbd> to ask
          </p>
          {running ? (
            <Button onClick={props.onCancel} size="sm" type="button" variant="outline">
              <SquareIcon aria-hidden />
              Stop
            </Button>
          ) : (
            <Button disabled={!draft.trim()} size="sm" type="submit">
              <SendIcon aria-hidden />
              Send
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}
